import React from 'react';
import type { CorrelationWidgetConfig, CorrelationSeriesType } from './types';
import { CORRELATION_SERIES_OPTIONS } from './types';

interface CorrelationWidgetBuilderProps {
  config: Partial<CorrelationWidgetConfig>;
  onChange: (config: CorrelationWidgetConfig) => void;
}

const CorrelationWidgetBuilder: React.FC<CorrelationWidgetBuilderProps> = ({ config, onChange }) => {
  const update = (patch: Partial<CorrelationWidgetConfig>) =>
    onChange({ ...config, ...patch } as CorrelationWidgetConfig);

  return (
    <div className="space-y-4">
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-sm text-warning-800">
        <strong>Correlation</strong>
        <p className="mt-1">Correlation analysis between two data series with rolling correlation chart</p>
      </div>

      {/* Variable A */}
      <fieldset className="border border-neutral-200 rounded-lg p-3 space-y-2">
        <legend className="text-xs font-semibold text-neutral-500 uppercase px-1">Variable A — Dashboard Asset</legend>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Series</label>
          <select
            value={config.series_a || 'price'}
            onChange={(e) => update({ series_a: e.target.value as CorrelationSeriesType })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-surface"
          >
            {CORRELATION_SERIES_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p className="text-xs text-neutral-400 mt-1">Entity is locked to the dashboard asset</p>
        </div>
      </fieldset>

      {/* Variable B */}
      <fieldset className="border border-neutral-200 rounded-lg p-3 space-y-2">
        <legend className="text-xs font-semibold text-neutral-500 uppercase px-1">Variable B</legend>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Series</label>
          <select
            value={config.series_b || 'price'}
            onChange={(e) => update({ series_b: e.target.value as CorrelationSeriesType })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm bg-surface"
          >
            {CORRELATION_SERIES_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Ticker
            <span className="font-normal text-neutral-400 ml-1">(optional)</span>
          </label>
          <input
            type="text"
            placeholder="defaults to dashboard asset"
            value={config.entity_b ?? ''}
            onChange={(e) => update({ entity_b: e.target.value || undefined })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-neutral-400 mt-1">Leave blank to use the same asset as Variable A</p>
        </div>
      </fieldset>

      {/* Shared params */}
      <fieldset className="border border-neutral-200 rounded-lg p-3 space-y-2">
        <legend className="text-xs font-semibold text-neutral-500 uppercase px-1">Parameters</legend>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Horizon <span className="font-normal text-neutral-400 ml-1">(trading days)</span>
          </label>
          <input
            type="number"
            placeholder="504"
            value={config.horizon ?? ''}
            onChange={(e) => update({ horizon: e.target.value ? parseInt(e.target.value, 10) : undefined })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-neutral-500 mt-1">Default: 504 trading days</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Correlation Window <span className="font-normal text-neutral-400 ml-1">(trading days)</span>
          </label>
          <input
            type="number"
            placeholder="60"
            value={config.correlation_window ?? ''}
            onChange={(e) => update({ correlation_window: e.target.value ? parseInt(e.target.value, 10) : undefined })}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-neutral-500 mt-1">Default: 60 trading days</p>
        </div>
      </fieldset>

      <div className="border rounded-lg p-4 bg-primary-50 border-primary-200">
        <h4 className="font-medium text-primary-800 mb-2">How it works</h4>
        <ul className="text-sm text-primary-700 space-y-1 ml-5 list-disc">
          <li>Variable A is always the dashboard asset — pick which series to use</li>
          <li>Variable B defaults to the same asset but you can override the ticker</li>
          <li>Computes daily log-returns and rolling Pearson correlation</li>
          <li>Shows both series on independent axes with correlation overlay</li>
          <li>Requires FMP credentials to be configured in Settings</li>
        </ul>
      </div>
    </div>
  );
};

export default CorrelationWidgetBuilder;
